import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink, MessageSquare, Star, Trash2 } from "lucide-react";
import api from "../../../../const/axios";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StarRating from "../../../Elements/StarRating";
import QuestionView, { QuestionViewData } from "../../../Elements/QuestionView";
import { fmtDate } from "./utils";

interface RatedQuestion {
  questionId: number;
  number: number | null;
  myRating: number;
  average: number;
  count: number;
  packageId: number | null;
  packageName: string | null;
  phaseName: string | null;
}

interface ActivityComment {
  id: number;
  kind: "question" | "theme";
  targetId: number;
  text: string;
  added: string;
  label: string | null;
  number: number | null;
}

interface PageResponse<T> {
  total: number;
  items: T[];
}

const PREVIEW_SIZE = 5;
const PAGE_SIZE = 10;

/** Fetches one page of a `{ total, items }` endpoint; `reload` re-fetches the current page. */
function usePagedList<T>(url: string, page: number, perPage: number, version: number) {
  const [data, setData] = useState<PageResponse<T> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(url, { params: { page, perPage } })
      .then(res => { if (!cancelled) setData({ total: res.data?.total ?? 0, items: res.data?.items ?? [] }); })
      .catch(() => { if (!cancelled) setData({ total: 0, items: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url, page, perPage, version]);

  return { data, setData, loading };
}

const Card: React.FC<{ title: string; icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, action, children }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
    <div className="flex items-center justify-between gap-2 mb-3">
      <p className="flex items-center gap-1.5 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
        {icon}
        {title}
      </p>
      {action}
    </div>
    {children}
  </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-sm text-gray-400 dark:text-gray-500">{text}</p>
);

const Spinner: React.FC = () => (
  <div className="flex justify-center py-4">
    <span className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ViewAllButton: React.FC<{ total: number; onClick: () => void }> = ({ total, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-indigo-300 dark:hover:text-indigo-200"
  >
    Hamısına bax ({total})
  </button>
);

const Pager: React.FC<{ page: number; total: number; perPage: number; onPage: (p: number) => void }> = ({ page, total, perPage, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  const btn = "inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none";
  return (
    <div className="flex items-center justify-center gap-2 pt-3">
      <button type="button" className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Əvvəlki səhifə">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{page} / {pages}</span>
      <button type="button" className={btn} disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Növbəti səhifə">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── Rated questions ──────────────────────────────────────────────────────────

/** The full question, fetched only when its accordion row is opened, plus a control to change the rating. */
const RatedQuestionDetails: React.FC<{ item: RatedQuestion; onRated: (patch: Partial<RatedQuestion>) => void }> = ({ item, onRated }) => {
  const [view, setView] = useState<QuestionViewData | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const isVerified: boolean = useSelector((state: any) => state.user?.data?.isVerified ?? false);

  useEffect(() => {
    let cancelled = false;
    api.get(`questions/${item.questionId}/view`)
      .then(res => { if (!cancelled) setView(res.data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [item.questionId]);

  const rate = async (value: number) => {
    setSaving(true);
    try {
      const res = await api.post(`questions/${item.questionId}/rate`, { rating: value });
      onRated({ myRating: value, average: res.data?.average ?? item.average, count: res.data?.count ?? item.count });
    } catch {
      // the badge keeps showing the previous rating
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-3 pl-1">
      {failed ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Sual yüklənmədi.</p>
      ) : !view ? (
        <Spinner />
      ) : (
        <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 px-4 py-3 text-sm">
          <QuestionView question={view.question} />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
        <StarRating
          average={item.average}
          count={item.count}
          myRating={item.myRating}
          onRate={rate}
          disabled={saving}
          loading={saving}
          canRate={isVerified}
        />
        <Link
          to={`/questions/${item.questionId}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-indigo-300 dark:hover:text-indigo-200"
        >
          Suala keç
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

const RatedQuestionList: React.FC<{
  perPage: number;
  paginated?: boolean;
  version: number;
  onLoaded?: (total: number) => void;
}> = ({ perPage, paginated, version, onLoaded }) => {
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const { data, setData, loading } = usePagedList<RatedQuestion>("users/me/rated-questions", page, perPage, version);

  useEffect(() => { if (data) onLoaded?.(data.total); }, [data, onLoaded]);
  useEffect(() => { setOpenId(null); }, [page]);

  const patchItem = (questionId: number, patch: Partial<RatedQuestion>) =>
    setData(prev => prev && { ...prev, items: prev.items.map(q => (q.questionId === questionId ? { ...q, ...patch } : q)) });

  if (!data) return <Spinner />;
  if (data.total === 0) return <Empty text="Hələ heç bir suala qiymət verməmisiniz." />;

  return (
    <>
      <ul className={cn("divide-y divide-gray-100 dark:divide-gray-800", loading && "opacity-60")}>
        {data.items.map(q => {
          const open = openId === q.questionId;
          return (
            <li key={q.questionId}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : q.questionId)}
                aria-expanded={open}
                className="group w-full flex items-center gap-3 py-2.5 text-left"
              >
                <span className="flex-shrink-0 inline-flex items-center gap-0.5 rounded-lg bg-amber-50 dark:bg-amber-950 px-1.5 py-0.5 text-xs font-bold text-amber-600 dark:text-amber-300 tabular-nums">
                  <Star className="w-3 h-3 fill-current" />
                  {q.myRating}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-indigo-300 transition-colors">
                    {q.packageName ?? "Sual"}
                  </span>
                  <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                    {[q.number ? `Sual ${q.number}` : null, q.phaseName].filter(Boolean).join(" · ")}
                    {q.count > 0 && ` · orta ${q.average.toFixed(1)} (${q.count})`}
                  </span>
                </span>
                <ChevronDown className={cn("w-4 h-4 flex-shrink-0 text-gray-400 transition-transform", open && "rotate-180")} />
              </button>
              {open && <RatedQuestionDetails item={q} onRated={patch => patchItem(q.questionId, patch)} />}
            </li>
          );
        })}
      </ul>
      {paginated && <Pager page={page} total={data.total} perPage={perPage} onPage={setPage} />}
    </>
  );
};

// ── Comments ─────────────────────────────────────────────────────────────────

const CommentList: React.FC<{
  perPage: number;
  paginated?: boolean;
  version: number;
  onLoaded?: (total: number) => void;
  onDeleted?: () => void;
}> = ({ perPage, paginated, version, onLoaded, onDeleted }) => {
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data, loading } = usePagedList<ActivityComment>("users/me/comments", page, perPage, version + reload);

  useEffect(() => { if (data) onLoaded?.(data.total); }, [data, onLoaded]);

  const remove = async (c: ActivityComment) => {
    const key = `${c.kind}-${c.id}`;
    setDeletingKey(key);
    setError(null);
    try {
      await api.delete(c.kind === "theme" ? `themecomments/${c.id}` : `questioncomments/${c.id}`);
      setConfirmKey(null);
      // Step back if this emptied the last page, then refetch so the page refills.
      if (data && data.items.length === 1 && page > 1) setPage(page - 1);
      else setReload(r => r + 1);
      onDeleted?.();
    } catch {
      setError("Şərh silinmədi. Yenidən cəhd edin.");
    } finally {
      setDeletingKey(null);
    }
  };

  if (!data) return <Spinner />;
  if (data.total === 0) return <Empty text="Hələ şərh yazmamısınız." />;

  return (
    <>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <ul className={cn("divide-y divide-gray-100 dark:divide-gray-800", loading && "opacity-60")}>
        {data.items.map(c => {
          const key = `${c.kind}-${c.id}`;
          const confirming = confirmKey === key;
          return (
            <li key={key} className="flex items-start gap-2 py-2.5">
              <Link
                to={c.kind === "theme" ? `/themes/${c.targetId}` : `/questions/${c.targetId}`}
                className="group block min-w-0 flex-1"
              >
                <span className="block text-sm text-gray-800 dark:text-gray-100 line-clamp-2 break-words group-hover:text-blue-600 dark:group-hover:text-indigo-300 transition-colors">
                  {c.text}
                </span>
                <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                  {[c.kind === "theme" ? "Mövzu" : c.number ? `Sual ${c.number}` : "Sual", c.label, fmtDate(c.added)].filter(Boolean).join(" · ")}
                </span>
              </Link>
              {confirming ? (
                <span className="flex-shrink-0 flex items-center gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => remove(c)}
                    disabled={deletingKey === key}
                    className="rounded-lg bg-red-600 px-2 py-1 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    {deletingKey === key ? "Silinir..." : "Sil"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmKey(null)}
                    className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    Ləğv et
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmKey(key)}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                  aria-label="Şərhi sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {paginated && <Pager page={page} total={data.total} perPage={perPage} onPage={setPage} />}
    </>
  );
};

// ── Profile cards ────────────────────────────────────────────────────────────

const ListDialog: React.FC<{ title: string; open: boolean; onOpenChange: (open: boolean) => void; children: React.ReactNode }> = ({ title, open, onOpenChange, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

/** The signed-in user's own rated questions and comments (own profile only). */
const ProfileActivity: React.FC = () => {
  const [ratedTotal, setRatedTotal] = useState(0);
  const [commentTotal, setCommentTotal] = useState(0);
  const [ratedOpen, setRatedOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Bumped when a dialog closes (or a comment is deleted) so the preview reflects changes made there.
  const [ratedVersion, setRatedVersion] = useState(0);
  const [commentVersion, setCommentVersion] = useState(0);

  const onRatedOpenChange = useCallback((open: boolean) => {
    setRatedOpen(open);
    if (!open) setRatedVersion(v => v + 1);
  }, []);
  const onCommentsOpenChange = useCallback((open: boolean) => {
    setCommentsOpen(open);
    if (!open) setCommentVersion(v => v + 1);
  }, []);

  return (
    <>
      <Card
        title="Ən yüksək qiymət verdiyim suallar"
        icon={<Star className="w-3.5 h-3.5" />}
        action={ratedTotal > PREVIEW_SIZE && <ViewAllButton total={ratedTotal} onClick={() => setRatedOpen(true)} />}
      >
        <RatedQuestionList perPage={PREVIEW_SIZE} version={ratedVersion} onLoaded={setRatedTotal} />
      </Card>

      <Card
        title="Son şərhlərim"
        icon={<MessageSquare className="w-3.5 h-3.5" />}
        action={commentTotal > PREVIEW_SIZE && <ViewAllButton total={commentTotal} onClick={() => setCommentsOpen(true)} />}
      >
        <CommentList perPage={PREVIEW_SIZE} version={commentVersion} onLoaded={setCommentTotal} />
      </Card>

      <ListDialog title="Qiymət verdiyim suallar" open={ratedOpen} onOpenChange={onRatedOpenChange}>
        <RatedQuestionList perPage={PAGE_SIZE} paginated version={0} />
      </ListDialog>

      <ListDialog title="Şərhlərim" open={commentsOpen} onOpenChange={onCommentsOpenChange}>
        <CommentList perPage={PAGE_SIZE} paginated version={0} onLoaded={setCommentTotal} />
      </ListDialog>
    </>
  );
};

export default ProfileActivity;
